/**
 * The words and pictures on the welcome tour's steps (WelcomeTour.tsx), kept apart so tests can read
 * them.
 *
 * Every line here was checked against the website, the API and the desktop code before it went in
 * (see the PR that added this tour). Keep them that way: if something stops being true, take it out
 * rather than soften it. Pictures are stills from the help articles (public/help/<slug>/), which show
 * only the invented demo facility; the alt text and the caption are the article step's own.
 */
import { findArticle } from '../../help/articles';

/** A still from a help article: the article's slug and the step's id. */
export interface TourPictureRef {
  slug: string;
  step: string;
}

export interface TourItem {
  /** The bold name. */
  name: string;
  /** One short line under the name in a selectable list. */
  short: string;
  /** One or two plain sentences: what it is and what it means for your day. */
  text: string;
  /** A Bootstrap icon class, drawn small beside the name. */
  icon: string;
  /**
   * On a phone, in place of `name`, `short` and `text` where the screen differs. An item with these
   * shows no picture on a phone, since the stills are of the full-size screen.
   */
  phoneName?: string;
  phoneShort?: string;
  phoneText?: string;
  /** The help article to read, linked at the end as "Read: <its title>". */
  read?: string;
  picture?: TourPictureRef;
}

/** The line under "What stays the same": facility data is shared, so nothing is entered again. */
export const SAME_LEAD = 'Your Dietech information is already here. You don\'t need to start over.';

/** What stays the same (desktop users). */
export const SAME: TourItem[] = [
  {
    // The API opens the facility's own database from the same connection list the desktop signs in with.
    name: 'Your facility\'s information',
    short: 'Shared with the desktop',
    icon: 'bi-building',
    text: 'The website and the desktop share it. A resident you admit on the website is on the desktop\'s resident list too, and the other way round.',
  },
  {
    name: 'Your residents, diet orders and menus',
    short: 'The ones you already have',
    icon: 'bi-people',
    text: 'Your residents, diet orders, menus, items, recipes and tables are the ones you already have. Nothing has to be entered again.',
    read: 'finding-a-resident',
  },
  {
    // Both read the same role record (tblPM_Security_Roles); the menu lists only the pages the role opens.
    name: 'Your role',
    short: 'The same rights as on the desktop',
    icon: 'bi-person-badge',
    text: 'It decides what you can see and change, the same as on the desktop. If a page you need is missing, ask your administrator.',
  },
  {
    // Both read and write tblPMSec_User.DF_Start_Form and DF_Dietech_Facility; the desktop reads them at sign-in.
    name: 'Your start page and default facility',
    short: 'Shared with the desktop',
    icon: 'bi-house-door',
    text: 'They are the ones saved for you on the desktop. Change either one on the website and the desktop uses it the next time you sign in there.',
    read: 'settings-and-preferences',
  },
  {
    // Both read and write tblPM_User_ReportFavorite.
    name: 'Your favorite reports',
    short: 'Shared with the desktop',
    icon: 'bi-star',
    text: 'The reports in your desktop Favorites are your favorites here too. Star a report on the website and it joins your desktop Favorites.',
    read: 'favorites-and-print-queue',
  },
];

/** The one picture on "What stays the same". */
export const SAME_PICTURE: TourPictureRef = { slug: 'favorites-and-print-queue', step: 'favorites-filter' };

/** The menu (desktop users read it as different, new users as what they can do). */
const MENU: TourItem = {
  name: 'The menu on the left',
  short: 'Every page, by section',
  icon: 'bi-layout-sidebar',
  text: 'Instead of the desktop\'s ribbon, every page is in the menu on the left. Select a section to see its pages, and pin the pages you use every day.',
  // Below 768px the menu is the tab bar and More (MobileTabBar, MobileMoreMenu).
  phoneName: 'The tabs at the bottom',
  phoneShort: 'Each section, and More',
  phoneText: 'Instead of the desktop\'s ribbon, the tabs at the bottom of the screen open each section. More has the rest.',
  read: 'getting-around',
  // open-page shows the menu open on Residents with its pages listed (open-section's still was taken
  // before the list drew).
  picture: { slug: 'getting-around', step: 'open-page' },
};

/** Reports (desktop users read it as different, new users as what they can do). */
const REPORTS: TourItem = {
  // Generate queues the report (GenericReportForm), a message says when it is ready (View), the viewer
  // prints and exports PDF, Excel and Word (ReportPdfDialog), and the Print Queue lists it.
  name: 'Reports and the Print Queue',
  short: 'Print, or save as PDF, Excel or Word',
  icon: 'bi-file-earmark-text',
  text: 'A report is made while you keep working, and a message tells you when it is ready. It opens in a viewer, where you print it or export it as a PDF, Excel or Word file. The Print Queue keeps it, so you can print it again later.',
  read: 'running-a-report',
  picture: { slug: 'running-a-report', step: 'save' },
};

/** What is different (desktop users). */
export const DIFFERENT: TourItem[] = [
  {
    name: 'In your web browser',
    short: 'Nothing to install',
    icon: 'bi-window',
    text: 'Dietech Online runs in your web browser, so there is nothing to install to use it.',
  },
  MENU,
  REPORTS,
  {
    // Printing from the website opens the browser's print window (tray-tickets article); print
    // schedules go through the print agent (PrintScheduleService). Automatic printing is off in production
    // (no PrintScheduleSettings override), so name Print Now, which works today, as h60 does.
    name: 'Tray tickets',
    short: 'Preview on screen, then print',
    icon: 'bi-receipt',
    text: 'Preview any resident\'s ticket on screen first. Tickets you print open in your browser\'s print window, where you choose the printer. Tickets that print without anyone opening them, such as Print Now in Selective Touch, go to your kitchen\'s printers through your facility\'s print agent, a small program on one of your facility\'s computers.',
    read: 'tray-tickets',
    picture: { slug: 'tray-tickets', step: 'read' },
  },
];

/*
 * What the website has that the desktop does not (everyone). Each was checked against the desktop: it
 * has no per-screen help (its help file is never opened), only tree and grid filters (no search across
 * pages), and no way to set up a tablet. The held EHR diet changes card was left out: the desktop
 * dashboard shows held diet orders too.
 */
const HELP: TourItem = {
  name: 'Help on every page',
  short: 'Step-by-step articles',
  icon: 'bi-question-circle',
  text: 'Select Help at the top of any page for help with that page. The articles go step by step, with a picture for each step.',
  read: 'getting-around',
  picture: { slug: 'getting-around', step: 'help' },
};

const SEARCH: TourItem = {
  // GlobalSearch: Pages, Reports, residents, Items, Recipes (the ones the role opens); Ctrl+/ (TopToolbar).
  name: 'Search',
  short: 'Pages, reports, residents, items, recipes',
  icon: 'bi-search',
  text: 'Type in the search box at the top of any page to go straight to a page, report, resident, item or recipe. Ctrl+/ takes you to the box.',
  read: 'finding-a-resident',
  picture: { slug: 'getting-around', step: 'search-page' },
};

const TABLETS: TourItem = {
  // TabletApp: register with the code from Facility Settings > Selective Touch Tablets, then a PIN pad.
  name: 'Selective Touch on a tablet',
  short: 'Menu orders on a tablet',
  icon: 'bi-tablet',
  text: 'Set a tablet up once in Facility Settings, under Selective Touch Tablets. Staff unlock it with their PIN and take residents\' menu choices on it.',
  read: 'selective-touch',
  picture: { slug: 'selective-touch', step: 'resident' },
};

/** The line under "What's new" (desktop users). */
export const NEW_LEAD = 'Things the website has that the desktop does not.';

/** What is new (desktop users). */
export const NEW_ITEMS: TourItem[] = [HELP, SEARCH, TABLETS];

/** What you can do (new users): the menu and reports first, then what is new. */
export const NEW_USER_ITEMS: TourItem[] = [
  {
    ...MENU,
    text: 'Every page is in the menu on the left. Select a section to see its pages, and pin the pages you use every day.',
    phoneText: 'The tabs at the bottom of the screen open each section. More has the rest.',
  },
  REPORTS,
  HELP,
  SEARCH,
  TABLETS,
];

/** Every item on the lists, for the tests. */
export const ALL_ITEMS: TourItem[] = [...SAME, ...DIFFERENT, ...NEW_ITEMS, ...NEW_USER_ITEMS];

/** Every picture the tour shows, for the tests. */
export const ALL_PICTURES: TourPictureRef[] = [
  SAME_PICTURE,
  ...ALL_ITEMS.map((i) => i.picture).filter((p): p is TourPictureRef => !!p),
];

export interface TourPicture {
  src: string;
  width: number;
  height: number;
  alt: string;
  /** "<step heading> · <article title>". */
  caption: string;
}

/** The still a picture reference points at, with the article step's alt text and heading. */
export function resolvePicture(ref: TourPictureRef | undefined): TourPicture | undefined {
  if (!ref) return undefined;
  const article = findArticle(ref.slug);
  const step = article?.steps.find((s) => s.id === ref.step);
  if (!article || !step?.media) return undefined;
  const { image, alt } = step.media;
  return { src: image.src, width: image.width, height: image.height, alt, caption: `${step.heading} · ${article.title}` };
}

/** A help article's title, for "Read: <title>". */
export function articleTitle(slug: string): string | undefined {
  return findArticle(slug)?.title;
}
